import {
  diamondSpanInsetBy,
  HAZARD_LABEL_INNER_BORDER_INSET_RATIO,
  HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO,
  HAZARD_LABEL_LOWER_TEXT_LINE_GAP_RATIO,
  HAZARD_LABEL_LOWER_TEXT_NAME_CAP_RATIO,
  HAZARD_LABEL_LOWER_TEXT_NOTICE_CAP_RATIO,
  HAZARD_LABEL_LOWER_TEXT_TOP_RATIO,
  HAZARD_LABEL_STRIPE_COUNT,
  HAZARD_LABEL_SYMBOL_CENTER_RATIO,
  HAZARD_LABEL_SYMBOL_SIZE_RATIO,
  HAZARD_LABEL_SYMBOL_WIDTH_RATIO,
  HAZARD_LABEL_UPPER_DIAMOND_POINT_RATIO,
  HAZARD_LABEL_UPPER_DIAMOND_SYMBOL_RATIO,
  HazardLabelBackground,
  HazardLabelDesign,
} from "./hazardClassDiamond";

/**
 * The shapes and colours a hazard label is drawn from, as plain data.
 *
 * The PDF draws with react-pdf primitives and Oak's preview draws with DOM
 * ones, but both draw SVG, and an SVG path string means the same thing to
 * each. Keeping the geometry here rather than in either renderer is what
 * stops the two drifting: the corner radius, the inner border and the stripe
 * run all have one definition, and a correction lands in both places at once.
 *
 * Why the shapes are what they are -- the clip path in final orientation, the
 * corner curve that falls short of its control point -- is in
 * docs/waste/dot-label-rendering.md.
 */

const WHITE = "#FFFFFF";
const BLACK = "#000000";

/** The diamond's outer border. 49 CFR 172.407(d)(2): the border is shown in
 * black. */
export const HAZARD_LABEL_OUTLINE_COLOR = BLACK;

/**
 * The design's diamond as an SVG path, with its points rounded by `radius`.
 *
 * A path in final orientation, NOT a rotated square: rotating one turns
 * class 8's horizontal split into a diagonal bar and class 4.1's vertical
 * stripes into diagonal ones.
 *
 * Each point is rounded by pulling back along both edges that meet there and
 * bridging them with a quadratic curve through the point itself. A quadratic
 * curve never reaches its control point, so a large radius pulls the drawn
 * shape inside its nominal span. `diamondDrawnSpan` says by how much, for a
 * caller that has to hit a measured size.
 */
export function diamondPath(size: number, radius: number, offset = 0): string {
  const parts: string[] = [];
  diamondCorners(size, radius, offset).forEach((c, index) => {
    parts.push(
      `${index === 0 ? "M" : "L"} ${c.enter[0]} ${c.enter[1]}`,
      `Q ${c.point[0]} ${c.point[1]} ${c.exit[0]} ${c.exit[1]}`
    );
  });
  parts.push("Z");
  return parts.join(" ");
}

type Point = [number, number];

/** One rounded point: where the curve starts and ends, and the point itself. */
interface DiamondCorner {
  enter: Point;
  point: Point;
  exit: Point;
}

/** The four rounded points of `diamondPath`: top, right, bottom, left. */
function diamondCorners(size: number, radius: number, offset: number): DiamondCorner[] {
  const mid = size / 2 + offset;
  const inset = 1 + offset;
  const points: Point[] = [
    [mid, inset],
    [size - 1 + offset, mid],
    [mid, size - 1 + offset],
    [inset, mid],
  ];
  // Pull-back distance along an edge, as a fraction of that edge's length.
  const edge = Math.hypot(mid - inset, mid - inset);
  const t = Math.min(radius, edge * 0.5) / edge;
  const lerp = (a: Point, b: Point, k: number): Point => [
    a[0] + (b[0] - a[0]) * k,
    a[1] + (b[1] - a[1]) * k,
  ];
  return points.map((point, index) => ({
    enter: lerp(point, points[(index + 3) % 4], t),
    point,
    exit: lerp(point, points[(index + 1) % 4], t),
  }));
}

/**
 * The lower half of `diamondPath`'s outline as an open path, from the right
 * point round the bottom to the left point. Each side point's curve is split
 * where it crosses the centre line (its t = 0.5).
 */
function lowerHalfDiamondPath(size: number, radius: number, offset: number): string {
  const [, right, bottom, left] = diamondCorners(size, radius, offset);
  const half = (a: Point, b: Point): Point => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const centreCrossing = (c: DiamondCorner): Point => [
    (c.enter[0] + 2 * c.point[0] + c.exit[0]) / 4,
    (c.enter[1] + 2 * c.point[1] + c.exit[1]) / 4,
  ];
  const fromRight = centreCrossing(right);
  const toLeft = half(left.enter, left.point);
  const rightControl = half(right.point, right.exit);
  return [
    `M ${fromRight[0]} ${fromRight[1]}`,
    `Q ${rightControl[0]} ${rightControl[1]} ${right.exit[0]} ${right.exit[1]}`,
    `L ${bottom.enter[0]} ${bottom.enter[1]}`,
    `Q ${bottom.point[0]} ${bottom.point[1]} ${bottom.exit[0]} ${bottom.exit[1]}`,
    `L ${left.enter[0]} ${left.enter[1]}`,
    `Q ${toLeft[0]} ${toLeft[1]} ${centreCrossing(left)[0]} ${centreCrossing(left)[1]}`,
  ].join(" ");
}

/**
 * The vertical stripes of a class 4.1 or class 9 design, across the diamond's
 * full width and `height` tall from the top. Stripes and gaps alternate and
 * are equal in width, so the run is 2n-1 units wide. Clipped to the diamond
 * by the caller.
 */
export function stripePaths(size: number, height = size): string[] {
  const stripeWidth = size / (HAZARD_LABEL_STRIPE_COUNT * 2 - 1);
  return Array.from({ length: HAZARD_LABEL_STRIPE_COUNT }, (_unused, index) => {
    const x = index * stripeWidth * 2;
    return `M ${x} 0 h ${stripeWidth} v ${height} h ${-stripeWidth} Z`;
  });
}

/**
 * The inner border's colour. 49 CFR 172.407(d)(2) makes the border black,
 * except that white may be used on a one-colour green, red or blue background.
 * On a solid design the border follows the number, so the white-number red,
 * green and blue labels keep a white border. Every other design is black,
 * including CORROSIVE: its white-number exception covers the text and number
 * only.
 */
export function innerBorderColor(design: HazardLabelDesign): string {
  return design.background.kind === "SOLID" ? design.textColor : BLACK;
}

function insetDiamondPath(size: number, inset: number, cornerRadiusRatio: number): string {
  const innerSpan = diamondSpanInsetBy(size, inset);
  return diamondPath(innerSpan, innerSpan * cornerRadiusRatio, (size - innerSpan) / 2);
}

/**
 * The outside edge of the inner border, 5 mm in from the label's edge per
 * 172.407(c)(1). Colour that stops at the border is filled to this path.
 */
export function innerBorderOutsidePath(size: number, cornerRadiusRatio: number): string {
  return insetDiamondPath(size, size * HAZARD_LABEL_INNER_BORDER_INSET_RATIO, cornerRadiusRatio);
}

/**
 * The path the inner border's line is stroked along. 172.407(c)(1) measures
 * the 5 mm to the OUTSIDE of the line, so the stroke's centre sits half the
 * line width further in than `innerBorderOutsidePath`. `lowerHalfOnly` gives
 * the open path below the centre line, for class 9, whose stripes run to the
 * label's edge across the upper half.
 */
export function innerBorderPath(
  size: number,
  cornerRadiusRatio: number,
  lowerHalfOnly = false
): string {
  const inset =
    size * (HAZARD_LABEL_INNER_BORDER_INSET_RATIO + HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO / 2);
  if (!lowerHalfOnly) return insetDiamondPath(size, inset, cornerRadiusRatio);
  const innerSpan = diamondSpanInsetBy(size, inset);
  return lowerHalfDiamondPath(innerSpan, innerSpan * cornerRadiusRatio, (size - innerSpan) / 2);
}

/** One filled shape of a design's background. */
export interface DiamondFill {
  d: string;
  color: string;
}

function rectPath(x: number, y: number, width: number, height: number): string {
  return `M ${x} ${y} h ${width} v ${height} h ${-width} Z`;
}

/** POISON GAS's upper diamond, as the centre line it stops above. */
function upperDiamondPointY(size: number): number {
  return size / 2 - size * HAZARD_LABEL_UPPER_DIAMOND_POINT_RATIO;
}

/**
 * The background as fills in paint order, drawn by the caller clipped to the
 * diamond. `innerBorder` is `innerBorderOutsidePath`'s outline.
 *
 * Two designs paint inside the inner border only. CORROSIVE's black stops at
 * the border (172.407(d)(7)), so the inner diamond is filled black and the
 * white top half painted over it. POISON GAS fills the inner diamond black and
 * paints white over everything below a V whose point sits
 * HAZARD_LABEL_UPPER_DIAMOND_POINT_RATIO above the centre. The V's arms run at
 * 45 degrees, the same as the inner border's edges, so the black that is left
 * is a square-on-point sharing the border's top point (172.416(b)).
 */
export function diamondFills(
  background: HazardLabelBackground,
  size: number,
  innerBorder: string
): DiamondFill[] {
  const whole = rectPath(0, 0, size, size);
  const topHalf = rectPath(0, 0, size, size / 2);
  if (background.kind === "SOLID") return [{ d: whole, color: background.color }];
  if (background.kind === "SPLIT") {
    const bottomHalf = rectPath(0, size / 2, size, size / 2);
    return background.bottomStopsAtInnerBorder === true
      ? [
          { d: whole, color: WHITE },
          { d: innerBorder, color: background.bottomColor },
          { d: topHalf, color: background.topColor },
        ]
      : [
          { d: topHalf, color: background.topColor },
          { d: bottomHalf, color: background.bottomColor },
        ];
  }
  if (background.kind === "STRIPED") {
    const height = background.upperHalfOnly === true ? size / 2 : size;
    return [
      { d: whole, color: background.groundColor },
      ...stripePaths(size, height).map((d) => ({ d, color: background.stripeColor })),
    ];
  }
  const pointY = upperDiamondPointY(size);
  const armEndY = pointY - size / 2;
  return [
    { d: whole, color: background.color },
    { d: innerBorder, color: background.diamondColor },
    {
      d: `M ${size / 2} ${pointY} L 0 ${armEndY} L 0 ${size} L ${size} ${size} L ${size} ${armEndY} Z`,
      color: background.color,
    },
  ];
}

/** Where the symbol draws, in the diamond's own units. */
export interface SymbolBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * The symbol's box, fitted to the symbol's own `aspect` (width over height).
 *
 * The slot is wider than it is tall: the corrosion symbol is over 2:1 and the
 * gas cylinder over 3:1, and a square slot sized by height shrank them badly.
 * POISON GAS puts its symbol in the centre of its upper diamond, small enough
 * to stay inside it.
 */
export function hazardLabelSymbolBox(
  design: HazardLabelDesign,
  size: number,
  aspect: number
): SymbolBox {
  const centred = (width: number, centerY: number): SymbolBox => ({
    top: centerY - width / aspect / 2,
    left: size / 2 - width / 2,
    width,
    height: width / aspect,
  });
  const inUpperDiamond = design.background.kind === "UPPER_DIAMOND";
  const boxHeight =
    size *
    (inUpperDiamond ? HAZARD_LABEL_UPPER_DIAMOND_SYMBOL_RATIO : HAZARD_LABEL_SYMBOL_SIZE_RATIO);
  const width = Math.min(boxHeight * HAZARD_LABEL_SYMBOL_WIDTH_RATIO, boxHeight * aspect);
  if (!inUpperDiamond) {
    return centred(width, size * (design.symbolCenterRatio ?? HAZARD_LABEL_SYMBOL_CENTER_RATIO));
  }
  // Halfway between the inner border's top point and the upper diamond's
  // lower point.
  const innerTop = size * HAZARD_LABEL_INNER_BORDER_INSET_RATIO * Math.SQRT2;
  return centred(width, (innerTop + upperDiamondPointY(size)) / 2);
}

/**
 * The span a path from `diamondPath` actually draws, point to point, for a
 * given nominal size and corner radius.
 *
 * The rounded points are quadratic curves through their corner, and such a
 * curve never reaches its control point. A caller that has to measure 100mm
 * per side asks this rather than assuming the nominal size prints.
 */
export function diamondDrawnSpan(size: number, radius: number): number {
  const mid = size / 2;
  const edge = Math.hypot(mid - 1, mid - 1);
  const pullBack = Math.min(radius, edge * 0.5);
  // A quadratic's extreme is at t = 0.5, where it sits a quarter of the way
  // from the control point to the chord: a quarter of the pull-back along
  // each of the two edges, which is pullBack / (2 * sqrt(2)) along the axis.
  // Both points pull in, so the span loses twice that.
  const shortfall = pullBack / Math.SQRT2;
  // The path insets its points 1 unit from the box on every side.
  return size - 2 - shortfall;
}

/** One line of a design's lower-half text, placed in the diamond's own units. */
export interface LowerTextLine {
  text: string;
  /** The capital letters' height. */
  capHeight: number;
  /** Where the capitals' top edge sits, from the diamond's top. */
  capTop: number;
  bold: boolean;
}

/**
 * The text a design prints between the centre line and the class number,
 * laid out top to bottom from HAZARD_LABEL_LOWER_TEXT_TOP_RATIO. The label
 * name's capitals are over 7.6 mm on the 100 mm label (172.407(c)(4)).
 */
export function hazardLabelLowerText(design: HazardLabelDesign, size: number): LowerTextLine[] {
  if (design.lowerText === undefined) return [];
  const gap = size * HAZARD_LABEL_LOWER_TEXT_LINE_GAP_RATIO;
  const lines: LowerTextLine[] = [];
  let top = size * HAZARD_LABEL_LOWER_TEXT_TOP_RATIO;
  const add = (texts: string[], capRatio: number, bold: boolean): void => {
    const capHeight = size * capRatio;
    for (const text of texts) {
      lines.push({ text, capHeight, capTop: top, bold });
      top += capHeight + gap;
    }
  };
  add(design.lowerText.nameLines, HAZARD_LABEL_LOWER_TEXT_NAME_CAP_RATIO, true);
  add(design.lowerText.noticeLines, HAZARD_LABEL_LOWER_TEXT_NOTICE_CAP_RATIO, false);
  return lines;
}
